/**
 * Enhanced ShareButton Component
 * Advanced sharing with analytics, custom messages, image generation, achievements, and story formats
 */

import { toast } from 'sonner';
import { getReferralLink, getBetaLandingLink } from '../config/app-config';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Share2,
  X,
  Twitter,
  Facebook,
  MessageCircle,
  Mail,
  Linkedin,
  Copy,
  Check,
  TrendingUp,
  TrendingDown,
  Users,
  Gift,
  Edit3,
  Download,
  Image as ImageIcon,
  Award,
  Flame,
  Target,
  Instagram,
  BarChart3,
} from 'lucide-react';
import { analytics } from '../lib/analytics';
import { getSharePopupFeatures } from '../utils/popup';
import { safeLocalStorageGet, safeReadJsonFromStorage } from '../lib/storage-utils';

interface ShareButtonProps {
  variant?: 'default' | 'prominent' | 'compact';
  currentRTI?: number;
  previousRTI?: number;
  streak?: number;
  partnerName?: string;
  showRTIShare?: boolean;
  className?: string;
}

// Analytics helper
const trackShare = (platform: string, customMessage: boolean) => {
  const shareAnalytics = safeReadJsonFromStorage<Record<string, { count: number; lastShared: string | null; customMessageCount: number }>>(
    'tension_share_analytics',
    {},
  );
  
  if (!shareAnalytics[platform]) {
    shareAnalytics[platform] = { count: 0, lastShared: null, customMessageCount: 0 };
  }
  
  shareAnalytics[platform].count += 1;
  shareAnalytics[platform].lastShared = new Date().toISOString();
  if (customMessage) {
    shareAnalytics[platform].customMessageCount += 1;
  }
  
  localStorage.setItem('tension_share_analytics', JSON.stringify(shareAnalytics));
  
  // Track in analytics system
  analytics.track('share_clicked', {
    platform,
    customMessage,
    timestamp: new Date().toISOString(),
  });
};

// Achievement detection
const detectAchievements = (streak?: number, currentRTI?: number, previousRTI?: number) => {
  const achievements = [];
  const checkins = safeReadJsonFromStorage<Array<unknown>>('tension_checkins', []);
  
  // Streak achievements
  if (streak && streak >= 7) achievements.push({ type: 'streak_7', emoji: '🔥', label: '7-Day Streak!' });
  if (streak && streak >= 14) achievements.push({ type: 'streak_14', emoji: '⚡', label: '14-Day Streak!' });
  if (streak && streak >= 30) achievements.push({ type: 'streak_30', emoji: '🏆', label: '30-Day Streak!' });
  if (streak && streak >= 100) achievements.push({ type: 'streak_100', emoji: '👑', label: '100-Day Streak!' });
  
  // RTI improvement achievements
  if (currentRTI && previousRTI && currentRTI < previousRTI) {
    const improvement = previousRTI - currentRTI;
    if (improvement >= 20) achievements.push({ type: 'big_improvement', emoji: '📈', label: 'Major Improvement!' });
    if (currentRTI <= 25) achievements.push({ type: 'stable_zone', emoji: '🎯', label: 'Stable Zone!' });
  }
  
  // Check-in milestones
  if (checkins.length >= 10) achievements.push({ type: 'checkins_10', emoji: '✅', label: '10 Check-ins!' });
  if (checkins.length >= 50) achievements.push({ type: 'checkins_50', emoji: '💪', label: '50 Check-ins!' });
  if (checkins.length >= 100) achievements.push({ type: 'checkins_100', emoji: '🌟', label: '100 Check-ins!' });
  
  return achievements;
};

// Generate share image (regular format)
const generateShareImage = async (
  currentRTI: number,
  streak?: number,
  improvement?: number
): Promise<string> => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630; // Standard social media image size
  const ctx = canvas.getContext('2d')!;
  
  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#9333ea'); // purple-600
  gradient.addColorStop(0.5, '#db2777'); // pink-600
  gradient.addColorStop(1, '#f97316'); // orange-500
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Add noise/texture overlay
  ctx.globalAlpha = 0.1;
  for (let i = 0; i < 1000; i++) {
    ctx.fillStyle = Math.random() > 0.5 ? '#fff' : '#000';
    ctx.fillRect(Math.random() * canvas.width, Math.random() * canvas.height, 2, 2);
  }
  ctx.globalAlpha = 1;
  
  // Helper function to draw rounded rectangle
  const roundRect = (x: number, y: number, width: number, height: number, radius: number) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };
  
  // White content box
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  roundRect(60, 60, canvas.width - 120, canvas.height - 120, 30);
  ctx.fill();
  
  // Title
  ctx.fillStyle = '#171717';
  ctx.font = 'bold 64px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('TENSION', canvas.width / 2, 160);
  
  // Subtitle
  ctx.fillStyle = '#525252';
  ctx.font = '32px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Relationship Intelligence Platform', canvas.width / 2, 210);
  
  // RTI Score (large)
  ctx.fillStyle = '#9333ea';
  ctx.font = 'bold 120px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText(`${currentRTI}`, canvas.width / 2, 360);
  
  ctx.fillStyle = '#737373';
  ctx.font = '28px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Current RTI Score', canvas.width / 2, 410);
  
  // Stats row
  let statsY = 490;
  ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  
  if (streak) {
    ctx.fillStyle = '#ea580c';
    ctx.textAlign = 'left';
    ctx.fillText('🔥', 200, statsY);
    ctx.fillStyle = '#171717';
    ctx.fillText(`${streak}-Day Streak`, 260, statsY);
  }
  
  if (improvement && improvement > 0) {
    ctx.fillStyle = '#16a34a';
    ctx.textAlign = 'right';
    ctx.fillText(`↓${improvement} Points`, canvas.width - 200, statsY);
  }
  
  // Footer
  ctx.fillStyle = '#a3a3a3';
  ctx.font = '24px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Track, understand, and improve relationship dynamics', canvas.width / 2, canvas.height - 80);
  
  return canvas.toDataURL('image/png');
};

// Generate story format image (9:16)
const generateStoryImage = async (
  currentRTI: number,
  streak?: number,
  achievement?: { emoji: string; label: string }
): Promise<string> => {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1920; // Instagram/Facebook story size
  const ctx = canvas.getContext('2d')!;
  
  // Helper function to draw rounded rectangle
  const roundRect = (x: number, y: number, width: number, height: number, radius: number) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };
  
  // Background gradient
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  gradient.addColorStop(0, '#9333ea');
  gradient.addColorStop(0.5, '#db2777');
  gradient.addColorStop(1, '#f97316');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Achievement badge (if any)
  if (achievement) {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.beginPath();
    ctx.arc(canvas.width / 2, 300, 150, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.font = '120px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(achievement.emoji, canvas.width / 2, 340);
    
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 48px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.fillText(achievement.label, canvas.width / 2, 520);
  }
  
  // Main RTI display
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  roundRect(80, 700, canvas.width - 160, 400, 40);
  ctx.fill();
  
  ctx.fillStyle = '#171717';
  ctx.font = 'bold 72px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('My RTI Score', canvas.width / 2, 820);
  
  ctx.fillStyle = '#9333ea';
  ctx.font = 'bold 160px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText(`${currentRTI}`, canvas.width / 2, 1000);
  
  // Streak badge
  if (streak) {
    ctx.fillStyle = 'rgba(234, 88, 12, 0.1)';
    roundRect(240, 1140, 600, 100, 50);
    ctx.fill();
    
    ctx.font = 'bold 56px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('🔥', 300, 1210);
    
    ctx.fillStyle = '#ea580c';
    ctx.fillText(`${streak} Day Streak`, 400, 1210);
  }
  
  // TENSION branding
  ctx.fillStyle = '#fff';
  ctx.font = 'bold 64px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('TENSION', canvas.width / 2, canvas.height - 300);
  
  ctx.font = '36px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('Relationship Intelligence', canvas.width / 2, canvas.height - 220);
  
  // Swipe up indicator
  ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.font = '32px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillText('↑ Swipe up to join', canvas.width / 2, canvas.height - 100);
  
  return canvas.toDataURL('image/png');
};

export function EnhancedShareButton({ 
  variant = 'default',
  currentRTI,
  previousRTI,
  streak,
  partnerName,
  showRTIShare = false,
  className,
}: ShareButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState(false);
  const [shareImage, setShareImage] = useState<string | null>(null);
  const [storyImage, setStoryImage] = useState<string | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [activeTab, setActiveTab] = useState<'social' | 'image' | 'story' | 'achievement'>('social');
  const [messageInitialized, setMessageInitialized] = useState(false);
  const [achievements, setAchievements] = useState<Array<{ type: string; emoji: string; label: string }>>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const hasAchievements = achievements.length > 0;

  // Calculate achievements once when modal opens or props change
  useEffect(() => {
    const currentAchievements = detectAchievements(streak, currentRTI, previousRTI);
    setAchievements(currentAchievements);
  }, [streak, currentRTI, previousRTI]);

  // Get referral link
  const getReferralLinkUrl = () => {
    const referralCode = safeLocalStorageGet('tension_referral_code');
    if (referralCode) {
      return getReferralLink(referralCode);
    }
    return getBetaLandingLink();
  };

  // Initialize custom message - only run once when modal opens
  useEffect(() => {
    if (showModal && !messageInitialized) {
      // Calculate achievements fresh to avoid stale closures
      const currentAchievements = detectAchievements(streak, currentRTI, previousRTI);
      const hasCurrentAchievements = currentAchievements.length > 0;
      
      let message = '';
      if (hasCurrentAchievements && currentAchievements[0]) {
        message = `${currentAchievements[0].emoji} ${currentAchievements[0].label}\n\nI'm using TENSION to track my relationship health. ${streak ? `${streak} days in a row! 🔥` : ''}\n\nIt's helping me understand tension before it becomes a problem.`;
      } else if (showRTIShare && currentRTI && previousRTI) {
        const improvement = previousRTI - currentRTI;
        if (improvement > 0) {
          message = `My relationship RTI improved by ${improvement} points! 📈\n\nTENSION helps me track relationship health and it's working! ${streak ? `${streak} day streak 🔥` : ''}`;
        } else {
          message = `I'm using TENSION to improve my relationship 💜\n\nIt helps me understand relationship tension before it becomes a problem. Check it out!`;
        }
      } else {
        message = `I'm using TENSION to improve my relationship 💜\n\nIt helps me understand relationship tension before it becomes a problem. Check it out!`;
      }
      
      setCustomMessage(message);
      setMessageInitialized(true);
    }
    
    // Reset when modal closes
    if (!showModal && messageInitialized) {
      setMessageInitialized(false);
    }
  }, [showModal, messageInitialized, currentRTI, previousRTI, streak, showRTIShare]);

  // Generate images when modal opens
  useEffect(() => {
    if (showModal && currentRTI && !shareImage) {
      const improvement = previousRTI && currentRTI < previousRTI ? previousRTI - currentRTI : undefined;
      generateShareImage(currentRTI, streak, improvement).then(setShareImage).catch(err => {
        console.error('Failed to generate share image:', err);
      });
    }
    
    if (showModal && currentRTI && !storyImage) {
      const currentAchievements = detectAchievements(streak, currentRTI, previousRTI);
      generateStoryImage(currentRTI, streak, currentAchievements[0]).then(setStoryImage).catch(err => {
        console.error('Failed to generate story image:', err);
      });
    }
  }, [showModal, currentRTI, streak, previousRTI, shareImage, storyImage]);

  // Share analytics
  type SharePlatformStats = { count: number; lastShared: string | null; customMessageCount: number };
  const shareAnalytics = safeReadJsonFromStorage<Record<string, SharePlatformStats>>('tension_share_analytics', {});
  const checkInsCount = useMemo(
    () => safeReadJsonFromStorage<Array<unknown>>('tension_checkins', []).length,
    [showModal],
  );
  const referralsCount = useMemo(
    () => parseInt(safeLocalStorageGet('tension_referrals_count') || '0', 10) || 0,
    [showModal],
  );
  const rewardsEarned = Math.floor(referralsCount / 3);
  const toNextReward = 3 - (referralsCount % 3 || 0);
  const totalShares = Object.values(shareAnalytics).reduce(
    (sum, platform) => sum + (platform?.count ?? 0),
    0,
  );
  const topPlatform = Object.entries(shareAnalytics).sort(
    (a, b) => (b[1]?.count ?? 0) - (a[1]?.count ?? 0),
  )[0];

  const popupFeatures = getSharePopupFeatures();

  const shareToTwitter = () => {
    const text = customMessage;
    const url = getReferralLinkUrl();
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}&hashtags=TENSION,Relationships`,
      '_blank',
      popupFeatures
    );
    trackShare('twitter', false);
    toast.success('Opening Twitter...');
  };

  const shareToFacebook = () => {
    const url = getReferralLinkUrl();
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(customMessage)}`,
      '_blank',
      popupFeatures
    );
    trackShare('facebook', false);
    toast.success('Opening Facebook...');
  };

  const shareToLinkedIn = () => {
    const url = getReferralLinkUrl();
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank',
      popupFeatures
    );
    trackShare('linkedin', false);
    toast.success('Opening LinkedIn...');
  };

  const shareToWhatsApp = () => {
    const text = `${customMessage}\n\n${getReferralLinkUrl()}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(text)}`,
      '_blank',
      popupFeatures
    );
    trackShare('whatsapp', false);
    toast.success('Opening WhatsApp...');
  };

  const shareViaEmail = () => {
    const subject = 'Check out TENSION - Relationship Intelligence Platform';
    const body = `${customMessage}\n\nCheck it out: ${getReferralLinkUrl()}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    trackShare('email', false);
    toast.success('Opening email client...');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getReferralLinkUrl());
    setCopied(true);
    trackShare('copy_link', false);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadImage = (imageData: string, filename: string) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = imageData;
    link.click();
    trackShare('download_image', false);
    toast.success('Image downloaded!');
  };

  const shareImageToSocial = (platform: string, imageUrl: string) => {
    // In a real app, you'd upload the image and get a URL
    // For now, we'll just download it and prompt the user
    downloadImage(imageUrl, `tension-share-${platform}.png`);
    toast.success(`Image ready! Upload to ${platform} manually.`, { duration: 5000 });
  };

  // Render button variants
  const renderButton = () => {
    if (variant === 'prominent') {
      return (
        <button
          onClick={() => setShowModal(true)}
          className={`group relative w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 p-[2px] rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${className || ''}`}
        >
          <div className="bg-white rounded-xl px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center transition-transform">
                <Share2 className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                  Share TENSION & Earn Rewards
                </div>
                <div className="text-xs text-neutral-600">
                  {hasAchievements ? `${achievements[0].emoji} Share your ${achievements[0].label}` : 'Get 1 free month for every 3 friends who join'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasAchievements ? <Award className="w-5 h-5 text-purple-600" /> : <Gift className="w-5 h-5 text-purple-600" />}
            </div>
          </div>
        </button>
      );
    }

    if (variant === 'compact') {
      return (
        <Button
          onClick={() => setShowModal(true)}
          className={`gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 ${className || ''}`}
          size="sm"
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      );
    }

    return (
      <Button
        onClick={() => setShowModal(true)}
        className={`gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 ${className || ''}`}
      >
        <Share2 className="w-4 h-4" />
        Share TENSION
      </Button>
    );
  };

  return (
    <>
      {renderButton()}

      {/* Enhanced Share Modal - align to top so header/close are visible; scrollable content */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-8 pb-8 animate-in fade-in duration-200 overflow-y-auto">
          <Card className="max-w-2xl w-full max-h-[calc(100vh-4rem)] overflow-y-auto p-6 relative animate-in zoom-in duration-200">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors z-10"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-6">
              {/* Header */}
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                  Share TENSION
                </h2>
                <p className="text-sm text-neutral-600">
                  Help your friends improve their relationships and earn rewards!
                </p>
              </div>

              {/* Achievements Banner */}
              {hasAchievements && (
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Award className="w-5 h-5 text-yellow-600" />
                    <span className="font-semibold text-yellow-900">Achievements Unlocked!</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {achievements.map((achievement, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-full text-sm font-medium">
                        {achievement.emoji} {achievement.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Analytics Toggle */}
              {totalShares > 0 && (
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="w-full flex items-center justify-between p-3 bg-neutral-50 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-neutral-600" />
                    <span className="text-sm font-medium text-neutral-700">Your Share Analytics</span>
                  </div>
                  <span className="text-xs text-neutral-500">{totalShares} total shares</span>
                </button>
              )}

              {/* Analytics Panel */}
              {showAnalytics && (
                <div className="bg-neutral-50 rounded-xl p-4 space-y-3">
                  <h4 className="font-medium text-neutral-900">Platform Breakdown</h4>
                  {Object.entries(shareAnalytics).map(([platform, data]: [string, any]) => (
                    <div key={platform} className="flex items-center justify-between">
                      <span className="text-sm capitalize text-neutral-700">{platform}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 bg-neutral-200 rounded-full h-2">
                          <div
                            className="bg-purple-600 h-2 rounded-full"
                            style={{ width: `${(data.count / totalShares) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-neutral-900 w-8 text-right">{data.count}</span>
                      </div>
                    </div>
                  ))}
                  {topPlatform && (
                    <p className="text-xs text-neutral-600 pt-2 border-t">
                      🏆 Your favorite: <strong className="capitalize">{topPlatform[0]}</strong>
                    </p>
                  )}
                </div>
              )}

              {/* Tab Navigation */}
              <div className="flex gap-2 border-b border-neutral-200">
                <button
                  onClick={() => setActiveTab('social')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'social'
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  Social Media
                </button>
                {currentRTI && (
                  <>
                    <button
                      onClick={() => setActiveTab('image')}
                      className={`px-4 py-2 font-medium text-sm transition-colors ${
                        activeTab === 'image'
                          ? 'text-purple-600 border-b-2 border-purple-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Share Image
                    </button>
                    <button
                      onClick={() => setActiveTab('story')}
                      className={`px-4 py-2 font-medium text-sm transition-colors ${
                        activeTab === 'story'
                          ? 'text-purple-600 border-b-2 border-purple-600'
                          : 'text-neutral-600 hover:text-neutral-900'
                      }`}
                    >
                      Story Format
                    </button>
                  </>
                )}
                {hasAchievements && (
                  <button
                    onClick={() => setActiveTab('achievement')}
                    className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                      activeTab === 'achievement'
                        ? 'text-purple-600 border-b-2 border-purple-600'
                        : 'text-neutral-600 hover:text-neutral-900'
                    }`}
                  >
                    Achievements
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-yellow-500 rounded-full" />
                  </button>
                )}
              </div>

              {/* Social Media Tab */}
              {activeTab === 'social' && (
                <>
                  {/* Custom Message Editor */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-neutral-700">Your Message</label>
                      <button
                        onClick={() => setEditingMessage(!editingMessage)}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
                      >
                        <Edit3 className="w-3 h-3" />
                        {editingMessage ? 'Preview' : 'Edit'}
                      </button>
                    </div>
                    
                    {editingMessage ? (
                      <textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                        rows={4}
                        placeholder="Customize your share message..."
                      />
                    ) : (
                      <div className="px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-700 whitespace-pre-wrap">
                        {customMessage}
                      </div>
                    )}
                  </div>

                  {/* Rewards Info */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <Gift className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-900">Earn Rewards</span>
                    </div>
                    <p className="text-sm text-purple-700">
                      For every <strong>3 active friends</strong> you refer, you get{' '}
                      <strong>1 month of Pro FREE</strong>! 🎉
                    </p>
                  </div>

                  {/* Social Share Buttons */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-neutral-700 block">
                      Share on Social Media
                    </label>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={shareToTwitter}
                        className="flex items-center justify-center gap-2 p-4 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white rounded-xl transition-colors font-medium"
                      >
                        <Twitter className="w-5 h-5" />
                        Twitter
                      </button>
                      
                      <button
                        onClick={shareToFacebook}
                        className="flex items-center justify-center gap-2 p-4 bg-[#4267B2] hover:bg-[#365899] text-white rounded-xl transition-colors font-medium"
                      >
                        <Facebook className="w-5 h-5" />
                        Facebook
                      </button>
                      
                      <button
                        onClick={shareToLinkedIn}
                        className="flex items-center justify-center gap-2 p-4 bg-[#0077B5] hover:bg-[#006399] text-white rounded-xl transition-colors font-medium"
                      >
                        <Linkedin className="w-5 h-5" />
                        LinkedIn
                      </button>
                      
                      <button
                        onClick={shareToWhatsApp}
                        className="flex items-center justify-center gap-2 p-4 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl transition-colors font-medium"
                      >
                        <MessageCircle className="w-5 h-5" />
                        WhatsApp
                      </button>
                    </div>

                    <button
                      onClick={shareViaEmail}
                      className="w-full flex items-center justify-center gap-2 p-4 bg-neutral-700 hover:bg-neutral-800 text-white rounded-xl transition-colors font-medium"
                    >
                      <Mail className="w-5 h-5" />
                      Share via Email
                    </button>
                  </div>

                  {/* Copy Link */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700 block">
                      Or Copy Your Referral Link
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={getReferralLinkUrl()}
                        readOnly
                        className="flex-1 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-sm font-mono"
                      />
                      <Button onClick={copyLink} className="gap-2 whitespace-nowrap">
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {/* Share Image Tab */}
              {activeTab === 'image' && shareImage && (
                <div className="space-y-4">
                  <div className="bg-neutral-50 rounded-xl p-4">
                    <img src={shareImage} alt="Share preview" className="w-full rounded-lg shadow-lg" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => downloadImage(shareImage, 'tension-share.png')}
                      className="gap-2"
                      variant="outline"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button
                      onClick={() => shareImageToSocial('social media', shareImage)}
                      className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      <ImageIcon className="w-4 h-4" />
                      Share Image
                    </Button>
                  </div>
                  
                  <p className="text-xs text-neutral-600 text-center">
                    Perfect for Twitter, Facebook, and LinkedIn (1200x630)
                  </p>
                </div>
              )}

              {/* Story Format Tab */}
              {activeTab === 'story' && storyImage && (
                <div className="space-y-4">
                  <div className="bg-neutral-50 rounded-xl p-4 flex justify-center">
                    <img src={storyImage} alt="Story preview" className="max-h-96 rounded-lg shadow-lg" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => downloadImage(storyImage, 'tension-story.png')}
                      className="gap-2"
                      variant="outline"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </Button>
                    <Button
                      onClick={() => shareImageToSocial('Instagram Stories', storyImage)}
                      className="gap-2 bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      <Instagram className="w-4 h-4" />
                      Share to Stories
                    </Button>
                  </div>
                  
                  <p className="text-xs text-neutral-600 text-center">
                    Optimized for Instagram & Facebook Stories (1080x1920)
                  </p>
                </div>
              )}

              {/* Achievement Tab */}
              {activeTab === 'achievement' && hasAchievements && (
                <div className="space-y-4">
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-6">
                    <div className="text-center space-y-4">
                      <div className="text-6xl">{achievements[0].emoji}</div>
                      <h3 className="text-2xl font-bold text-neutral-900">{achievements[0].label}</h3>
                      <p className="text-neutral-700">
                        {achievements[0].type.includes('streak') && `You've maintained a ${streak}-day check-in streak! 🔥`}
                        {achievements[0].type === 'big_improvement' && `Your RTI improved significantly! Keep it up! 📈`}
                        {achievements[0].type === 'stable_zone' && `You've reached the Stable Zone! Great work! 🎯`}
                        {achievements[0].type.includes('checkins') && `You've completed ${checkInsCount} check-ins! 💪`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700 block">
                      Share Your Achievement
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => {
                          const achievementMessage = `${achievements[0].emoji} ${achievements[0].label}\n\nI'm tracking my relationship health with TENSION and hitting milestones! ${getReferralLinkUrl()}`;
                          setCustomMessage(achievementMessage);
                          setActiveTab('social');
                        }}
                        className="flex items-center justify-center gap-2 p-4 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white rounded-xl transition-colors font-medium"
                      >
                        <Twitter className="w-5 h-5" />
                        Twitter
                      </button>
                      
                      <button
                        onClick={() => {
                          const achievementMessage = `${achievements[0].emoji} ${achievements[0].label}\n\nI'm tracking my relationship health with TENSION and hitting milestones! ${getReferralLinkUrl()}`;
                          setCustomMessage(achievementMessage);
                          setActiveTab('social');
                        }}
                        className="flex items-center justify-center gap-2 p-4 bg-[#4267B2] hover:bg-[#365899] text-white rounded-xl transition-colors font-medium"
                      >
                        <Facebook className="w-5 h-5" />
                        Facebook
                      </button>
                    </div>
                  </div>
                  
                  {achievements.length > 1 && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-neutral-700 block">
                        More Achievements
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {achievements.slice(1).map((achievement, i) => (
                          <span key={i} className="px-3 py-2 bg-white border border-neutral-200 rounded-lg text-sm">
                            {achievement.emoji} {achievement.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 pt-4 border-t">
                <div className="text-center">
                  <div className="text-xl font-bold text-purple-600">
                    {referralsCount}
                  </div>
                  <div className="text-xs text-neutral-600">Invited</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">
                    {rewardsEarned}
                  </div>
                  <div className="text-xs text-neutral-600">Rewards</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-orange-600">
                    {toNextReward}
                  </div>
                  <div className="text-xs text-neutral-600">To Next</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}